import { fmtEur } from "@/lib/kpis";
import { cn } from "@/lib/utils";

const pct = (v) => (v == null ? "—" : `${v.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`);
const days = (v) => (v == null ? "—" : `${Math.round(v).toLocaleString("fr-FR")} j`);

export function computeRatios(k) {
    const ca = k.chiffre_affaires;
    const ok = ca != null && ca !== 0;
    return {
        marge_ebe: ok && k.ebe != null ? (k.ebe / ca) * 100 : null,
        bfr_jours: ok && k.bfr != null ? (k.bfr / ca) * 365 : null,
        treso_nette: k.tresorerie != null && k.bfr != null ? k.tresorerie - k.bfr : null,
        treso_jours: ok && k.tresorerie != null ? (k.tresorerie / ca) * 365 : null,
    };
}

const RATIOS = [
    { key: "marge_ebe", label: "Marge d'EBE", hint: "EBE / CA", fmt: pct, tone: (v) => (v < 5 ? "bad" : v < 10 ? "warn" : "good") },
    { key: "bfr_jours", label: "BFR en jours de CA", hint: "BFR / CA × 365", fmt: days, tone: (v) => (v > 90 ? "bad" : v > 60 ? "warn" : "good") },
    { key: "treso_nette", label: "Trésorerie nette", hint: "Trésorerie − BFR", fmt: fmtEur, tone: (v) => (v < 0 ? "bad" : "good") },
    { key: "treso_jours", label: "Trésorerie en jours de CA", hint: "Trésorerie / CA × 365", fmt: days, tone: (v) => (v < 15 ? "bad" : v < 30 ? "warn" : "good") },
];

const TONE = { good: "text-emerald-700", warn: "text-amber-700", bad: "text-rose-700" };

export default function KpiRatios({ kpis }) {
    const ratios = kpis.map((k) => ({ annee: k.annee_fiscale, ...computeRatios(k) }));
    if (!ratios.some((r) => Object.values(r).some((v) => v != null && typeof v === "number"))) return null;
    return (
        <div data-testid="kpi-ratios" className="pt-4 border-t border-border">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Ratios calculés automatiquement</p>
            <div className="overflow-x-auto">
                <table data-testid="kpi-ratios-table" className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                            <th className="py-2 pr-4 font-medium">Ratio</th>
                            {ratios.map((r) => <th key={r.annee} className="py-2 px-3 font-medium text-right">{r.annee}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {RATIOS.map((row) => (
                            <tr key={row.key} data-testid={`ratio-row-${row.key}`}>
                                <td className="py-2.5 pr-4">
                                    <span className="font-medium text-foreground">{row.label}</span>
                                    <span className="block text-[11px] text-muted-foreground">{row.hint}</span>
                                </td>
                                {ratios.map((r) => {
                                    const v = r[row.key];
                                    return (
                                        <td key={r.annee} data-testid={`ratio-${row.key}-${r.annee}`} className={cn("py-2.5 px-3 text-right tabular-nums font-medium", v == null ? "text-muted-foreground" : TONE[row.tone(v)])}>
                                            {row.fmt(v)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
                Seuils indicatifs : marge d&apos;EBE &lt; 5 % et BFR &gt; 90 jours signalés en rouge ; trésorerie nette négative = BFR non couvert par la trésorerie.
            </p>
        </div>
    );
}
